import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import axios, { AxiosError } from "axios"


declare global {
  interface Window {
    env: {
      API_URL: string;
      USER_EMAIL: string;
      USER_PASSWORD: string;
      VPA_JWT_SECRET_KEY: string;
      CONSENT_TEXT: string;
      CONSENT_PDF: string;
    };
  }
}

interface Vpa {
  vpaId: number;
  vpa: string;
  providerName: string;
  merchantNameFromVpa: string | null;
  qrImage: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  utr:{
    transactionDate: string
  }
  has_consent: boolean
}

interface Tenant {
  tenantId: number;
  tenantName: string;
  principalId: number;
  programId: number;
  propertyId: number | null;
  wing: string;
  floor: string;
  tenantBrand: string;
  remark: string;
  storeImage: string;
  storeLogo: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  latitude: number;
  longitude: number | null;
  registrationDocument: string | null;
  storeCoordinator: string;
  email: string;
  phone: string;
  term_n_condition: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  vpaId: number;
  status: string;
  vpa: Vpa[];
  vpa_consent_text: string;
  transaction_date: string;
}

interface TenantVpaMappingState {
  error: any | null
  loading: boolean,
  mapping: Tenant[] | null,
  vpa_consent: VpaConsentRecord | null
}

interface VpaConsentRecord {
  utr: string;
  consented_date: string;
  tenantVpaId: number;
  name: string;
  updatedAt: string;
  createdAt: string;
  vpa_consent_text: string;
  term_n_condition: string;
}


// Initial state
const initialState: TenantVpaMappingState = {
    mapping:  null,
    error:null,
    loading: false,
    vpa_consent: null

}

export const fetchVpaTenantMapping = createAsyncThunk(
  "distributor/fetchVpaTenantMapping",
  async ( {
    vpaId, tenantId
  }: {  vpaId: number, tenantId: number  }, { rejectWithValue }) => {
    
    try {

      const token_response = await axios.post(
        `${window.env.API_URL}/api/v1/auth/login`,
        {
          email: window.env.USER_EMAIL,
          password: window.env.USER_PASSWORD
        },
      )

      if (token_response.status === 200) {
        const { _, tokens } = token_response.data
        const response = await axios.request({
          baseURL: `${window?.env.API_URL}/v1/programs/mappings?vpaId=${vpaId}&tenantId=${tenantId}`,
          timeout: 10000,
          headers: {
            Authorization: `Bearer ${tokens.access.token}`,
            "Content-Type": "application/json",
          },
          method: "GET",
        })

        return response.data
      }
      else {
        throw Error("Failed to Authenticate")
      }

    
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch tenants",
      )
    }
  },
)


export const verifyUTR = createAsyncThunk(
  "distributor/verifyUTR",
  async ( {
    vpaId, tenantId, utr, name, termCondition, consentText
  }: {  vpaId: number, tenantId: number, utr: string, name: string, termCondition: string|null, consentText: string }, { rejectWithValue }) => {
    
    try {

      const token_response = await axios.post(
        `${window.env.API_URL}/api/v1/auth/login`,
        {
          email: window.env.USER_EMAIL,
          password: window.env.USER_PASSWORD
        },
      )

      if (token_response.status === 200) {
        const { _, tokens } = token_response.data
        const data = JSON.stringify({
          utr,
          tenantId,
          vpaId,
          name,
          termCondition,
          consentText
        });
        const response = await axios.request({
          baseURL: `${window?.env.API_URL}/v1/programs/utr/consent`,
          timeout: 10000,
          headers: {
            Authorization: `Bearer ${tokens.access.token}`,
            "Content-Type": "application/json",
          },
          method: "POST",
          data:data
        })

        console.log(response.data)
        return response.data
      }
      else {
        throw Error("Failed to Authenticate")
      }

    
    } catch (error: any) {
      console.log(error?.response)
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch tenants",
      )
    }
  },
)

const tenantVpaMappingSlice = createSlice({
  name: "tenant_vpa_mapping",
  initialState,
  reducers: {
    clearTenantVpaMapping(state) {
      // state.mapping = null
      state.loading = false
      state.error = null
      state.vpa_consent = null
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchVpaTenantMapping.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchVpaTenantMapping.fulfilled,
        (state, action: PayloadAction<Tenant[]>) => {
          state.loading = false
          state.mapping = action.payload
        },
      )
      .addCase(fetchVpaTenantMapping.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || "Failed to get Mapping"
      })
      .addCase(verifyUTR.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(
        verifyUTR.fulfilled,
        (state, action: PayloadAction<VpaConsentRecord>) => {
          state.loading = false
          state.vpa_consent = action.payload
        },
      )
      .addCase(verifyUTR.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || "Failed to Verify"
      })
    
  },
})

export const { clearTenantVpaMapping } = tenantVpaMappingSlice.actions

// Export the reducer
export default tenantVpaMappingSlice.reducer
